import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { startTransition, useEffect, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";

import { atoms } from "#/lib/atoms/atoms";
import cookies from "#/lib/cookies";
import { queryClient } from "#/lib/query";
import queryKeys from "#/lib/query-keys";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchRequest } from "#/lib/stream-api";
import type { components } from "#/types.ts/swagger";

export const useOrgs = () => {
  const [mid] = useAtom(atoms.mid);
  const [accessToken] = useAtom(atoms.accessToken);

  const { data: myOrgs } = useSuspenseQuery<
    components["schemas"]["OrgWithMerchant"][]
  >({
    queryKey: queryKeys.orgs(accessToken),
    queryFn: async () => {
      if (!accessToken) return [];
      const response = await fetchRequest("/org");
      return response?.sort(
        (a, b) => a?.name?.localeCompare(b?.name || "") || "",
      );
    },
  });

  const orgId = useAtomValue(atoms.orgId);

  const merchant = useMemo(() => {
    const options = myOrgs?.map((o) => o.merchants).flat();
    return options?.find((m) => m._id === mid);
  }, [myOrgs, mid]);

  const org = useMemo(
    () => myOrgs?.find((o) => o._id === orgId),
    [myOrgs, orgId],
  );

  const router = useRouter();
  const pathname = usePathname();

  const setOrgId = useSetAtom(atoms.orgId);
  const setMid = useSetAtom(atoms.mid);
  const setCurrentMenu = useSetAtom(atoms.currentMenuId);
  const setCurrentLocation = useSetAtom(atoms.currentLocationId);

  useEffect(() => {
    if (!myOrgs || !accessToken) return;

    if (myOrgs.length === 0) {
      cookies.delete(cookies.options.org_id);
      cookies.delete(cookies.options.merchant_id);
      setOrgId(null);
      setMid(null);
      if (!pathname?.includes("/create-org")) {
        router.replace("/create-org");
      }
      return;
    }

    const currentOrgId = cookies.get(cookies.options.org_id);

    if (!currentOrgId) {
      const myOrg =
        myOrgs?.find((o) => o?.is_default) || myOrgs[myOrgs.length - 1];

      if (myOrg) {
        const currentMerchant = myOrg.merchants.find((m) => m._id === mid) || myOrg?.merchants?.[0];
        if (currentMerchant) {
          // Initial bootstrap: set cookies + atoms directly. The Suspense
          // queries that just resolved have fresh data — wiping the cache
          // (as setCurrentOrgMerchant does via queryClient.clear()) would
          // force every Suspense boundary back into its fallback, causing
          // the menu page to flicker between header skeleton ↔ blank
          // content while the cascade settles. Query keys keyed on
          // orgId/mid will refetch naturally as the atoms update;
          // /location/merchant uses merchant_id only in the header, so
          // refetch it explicitly. startTransition keeps the current UI
          // rendered while the dependent queries (subscription, integrations,
          // merchant, locations, ...) re-key and re-fetch.
          cookies.set(cookies.options.org_id, myOrg._id);
          cookies.set(cookies.options.merchant_id, currentMerchant._id);
          // Clear stale currentMenu/currentLocation alongside the org+mid
          // set so persisted localStorage IDs from a previous org can't leak
          // through (matches the old setCurrentOrgMerchant behavior). All
          // four setters share the same transition so the dependent
          // Suspense queries re-key once, not twice.
          startTransition(() => {
            setCurrentMenu(null);
            setCurrentLocation(null);
            setOrgId(myOrg._id);
            setMid(currentMerchant._id);
          });
          queryClient.refetchQueries({
            queryKey: queryKeys.locations(currentMerchant._id),
            type: "active",
          });
        }
      }
    }
  }, [
    myOrgs,
    mid,
    router,
    pathname,
    accessToken,
    setOrgId,
    setMid,
    setCurrentMenu,
    setCurrentLocation,
  ]);

  return {
    data: myOrgs,
    merchant,
    org,
  };
};
